import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, FileText, Send, RefreshCcw, History, X, Eye, GitBranch, Maximize2, Minimize2 } from "lucide-react";
import api, { API_BASE, formatErr } from "../lib/api";
import DrawingViewerModal from "../components/DrawingViewerModal";
import DependencyGraphView from "../components/DependencyGraphView";


/**
 * PdfPreviewModal — in-app fullscreen PDF viewer.
 *
 * Replaces the old "download → open in browser PDF" UX. Embeds the
 * file via <iframe> with a true Maximise toggle so engineers can
 * review equipment layouts, P&IDs and other A4/A1 deliverables
 * without leaving the platform.
 */
function PdfPreviewModal({ code, title, src, onClose }) {
  const [maximised, setMaximised] = useState(true);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div data-testid="pdf-preview-modal"
          style={{ position: "fixed", inset: 0, zIndex: 1500,
                    background: "rgba(0,0,0,0.78)",
                    display: "flex", alignItems: "center",
                    justifyContent: "center",
                    padding: maximised ? 0 : "4vh 4vw" }}>
      <div style={{ background: "#0F131C",
                     border: "1px solid var(--pf-border)",
                     borderRadius: maximised ? 0 : 6,
                     width: maximised ? "100vw" : "min(1400px, 92vw)",
                     height: maximised ? "100vh" : "92vh",
                     display: "flex", flexDirection: "column",
                     boxShadow: "0 20px 80px rgba(0,0,0,0.7)" }}>
        <header style={{ display: "flex", alignItems: "center",
                          gap: 12, padding: "10px 14px",
                          borderBottom: "1px solid var(--pf-border)",
                          background: "var(--pf-surface)" }}>
          <FileText size={14} color="var(--pf-spark)"/>
          <div style={{ flex: 1, color: "var(--pf-text)",
                          fontWeight: 600, fontSize: 13,
                          letterSpacing: 0.2 }}>
            {code ? <code style={{ color: "var(--pf-spark)",
                                     marginRight: 8 }}>{code}</code>
                  : null}
            {title || "Document"}
          </div>
          <a href={src} download
              data-testid="pdf-preview-download"
              className="btn btn-ghost"
              style={{ display: "inline-flex", alignItems: "center",
                        gap: 6, fontSize: 12, padding: "5px 12px",
                        color: "#FFB000", textDecoration: "none" }}>
            <Download size={12}/> Download
          </a>
          <button onClick={() => setMaximised(m => !m)}
                  data-testid="pdf-preview-toggle"
                  className="btn btn-ghost"
                  title={maximised ? "Restore" : "Maximise"}
                  style={{ padding: "5px 10px" }}>
            {maximised ? <Minimize2 size={13}/> : <Maximize2 size={13}/>}
          </button>
          <button onClick={onClose}
                  data-testid="pdf-preview-close"
                  className="btn btn-ghost"
                  title="Close (Esc)"
                  style={{ padding: "5px 10px",
                            color: "var(--pf-err)" }}>
            <X size={13}/>
          </button>
        </header>
        <iframe src={`${src}#view=FitH&toolbar=1&navpanes=0`}
                 title={title || code || "PDF preview"}
                 data-testid="pdf-preview-iframe"
                 style={{ flex: 1, width: "100%",
                          border: "none", background: "#fff" }}/>
      </div>
    </div>
  );
}


const STATUS_STYLES = {
  DRAFT: { bg: "#1F2937", color: "#9CA3AF", border: "#374151" },
  "FOR REVIEW": { bg: "#1E3A8A", color: "#93C5FD", border: "#3B82F6" },
  "FOR APPROVAL": { bg: "#7C2D12", color: "#FED7AA", border: "#F97316" },
  "FOR CONSTRUCTION": { bg: "#14532D", color: "#86EFAC", border: "#22C55E" },
  "FOR INFORMATION": { bg: "#1F2937", color: "#9CA3AF", border: "#374151" },
  "APPROVED FOR CONSTRUCTION": { bg: "#14532D", color: "#86EFAC", border: "#22C55E" },
};

const STATUS_OPTIONS = [
  "ALL", "DRAFT", "FOR REVIEW", "FOR APPROVAL", "FOR CONSTRUCTION",
  "FOR INFORMATION", "APPROVED FOR CONSTRUCTION",
];

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.DRAFT;
  return (
    <span
      data-testid={`doc-status-${status.replace(/\s+/g, "-").toLowerCase()}`}
      style={{
        background: s.bg, color: s.color, border: `1px solid ${s.border}`,
        padding: "3px 8px", borderRadius: 999, fontSize: 11,
        fontWeight: 600, letterSpacing: 0.3, whiteSpace: "nowrap",
      }}
    >
      {status}
    </span>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div
      style={{
        background: "var(--pf-surface, #0F172A)",
        border: "1px solid var(--pf-border, #1F2937)",
        borderRadius: 8, padding: "12px 14px", flex: 1, minWidth: 140,
      }}
    >
      <div style={{ fontSize: 10, color: "#9CA3AF", letterSpacing: 1,
                       textTransform: "uppercase", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700,
                       color: accent || "#F3F4F6" }}>
        {value}
      </div>
    </div>
  );
}

export default function DocumentsRegister() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const [register, setRegister] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [healthScore, setHealthScore] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [discFilter, setDiscFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [transmittalBusy, setTransmittalBusy] = useState(false);
  const [transmittalResult, setTransmittalResult] = useState(null);
  const [txForm, setTxForm] = useState({
    purpose: "For Review",
    to_company: "",
    to_person: "",
    from_person: "Document Controller",
  });
  // Revision workflow modal state
  const [revModal, setRevModal] = useState(null); // { code, name, currentRev }
  const [revBusy, setRevBusy] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null); // { code, title, src }
  const [revStatus, setRevStatus] = useState("FOR REVIEW");
  const [revNote, setRevNote] = useState("");
  // Revision history + diff viewer state
  const [historyModal, setHistoryModal] = useState(null); // { code, name, revisions, selectedFrom, selectedTo }
  const [historyBusy, setHistoryBusy] = useState(false);
  const [diffData, setDiffData] = useState(null);
  const [diffBusy, setDiffBusy] = useState(false);
  // Bulk-issue modal state
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkForm, setBulkForm] = useState({
    target_status: "FOR CONSTRUCTION",
    transmittal_purpose: "For Construction",
    eligible_status: "FOR APPROVAL",
    to_company: "",
    to_person: "",
    from_person: "Document Controller",
    note: "",
  });
  // Drawing viewer state
  const [viewer, setViewer] = useState(null); // { title, subtitle, src? items? }
  // Editor↔Generator sync state (File 45)
  const [staleSync, setStaleSync] = useState({ total: 0, items: [] });
  const [regenBusy, setRegenBusy] = useState(false);
  const [showDepGraph, setShowDepGraph] = useState(false);

  const loadStale = async () => {
    try {
      const r = await api.get(`/projects/${projectId}/sync/stale`);
      setStaleSync(r.data || { total: 0, items: [] });
    } catch {
      setStaleSync({ total: 0, items: [] });
    }
  };

  const regenerateAllStale = async () => {
    if (!staleSync.total) return;
    if (!window.confirm(
      `Regenerate ${staleSync.total} stale deliverable(s)?\n\n` +
      `This queues them for the next simulation pipeline run, ` +
      `clearing the stale flag.`,
    )) return;
    setRegenBusy(true);
    try {
      const r = await api.post(
        `/projects/${projectId}/sync/regenerate`, {});
      alert(
        `Queued ${r.data?.codes?.length || 0} deliverables for regeneration.\n` +
        `Run the simulation pipeline to refresh their content.`,
      );
      await loadStale();
      await load();
    } catch (e) {
      alert(`Regenerate failed: ${formatErr(e.response?.data?.detail) || e.message}`);
    } finally {
      setRegenBusy(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get(`/projects/${projectId}/documents-register`);
      setRegister(r.data);
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
    // Health score is independent — run in parallel, swallow errors
    api.get(`/projects/${projectId}/health-score`)
      .then((r) => setHealthScore(r.data))
      .catch(() => setHealthScore(null));
  };

  useEffect(() => { load(); loadStale(); /* eslint-disable-next-line */ }, [projectId]);

  const filteredRows = useMemo(() => {
    const rows = register?.rows || [];
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "ALL" && r.status !== statusFilter) return false;
      if (discFilter !== "ALL" && r.discipline !== discFilter) return false;
      if (q && !`${r.code} ${r.name} ${r.doc_number}`.toLowerCase().includes(q))
        return false;
      return true;
    });
  }, [register, statusFilter, discFilter, search]);

  const disciplines = useMemo(() => {
    const set = new Set((register?.rows || []).map((r) => r.discipline));
    return ["ALL", ...Array.from(set).sort()];
  }, [register]);

  const allSelected = filteredRows.length > 0 &&
      filteredRows.every((r) => selected.has(r.code));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) filteredRows.forEach((r) => next.delete(r.code));
    else filteredRows.forEach((r) => next.add(r.code));
    setSelected(next);
  };
  const toggleOne = (code) => {
    const next = new Set(selected);
    if (next.has(code)) next.delete(code); else next.add(code);
    setSelected(next);
  };

  const generateTransmittal = async () => {
    if (selected.size === 0) {
      alert("Select at least one document to include in the transmittal.");
      return;
    }
    setTransmittalBusy(true);
    setTransmittalResult(null);
    try {
      const r = await api.post(`/projects/${projectId}/transmittal`, {
        transmittal_no: 1,
        purpose: txForm.purpose,
        to_company: txForm.to_company,
        to_person: txForm.to_person,
        from_person: txForm.from_person,
        codes: Array.from(selected),
      });
      setTransmittalResult(r.data);
      await load();
    } catch (e) {
      alert("Transmittal generation failed: " +
            (formatErr(e.response?.data?.detail) || e.message));
    } finally {
      setTransmittalBusy(false);
    }
  };

  const pdfHref = (code) =>
    `${API_BASE}/projects/${projectId}/deliverables/${encodeURIComponent(code)}/pdf`;
  const packageHref =
    `${API_BASE}/projects/${projectId}/engineering-package`;

  const openRevModal = (row) => {
    setRevModal({ code: row.code, name: row.name, currentRev: row.revision });
    // pre-select sensible next status based on current
    const next = {
      DRAFT: "FOR REVIEW",
      "FOR REVIEW": "FOR APPROVAL",
      "FOR APPROVAL": "FOR CONSTRUCTION",
      "FOR CONSTRUCTION": "APPROVED FOR CONSTRUCTION",
    }[row.status] || "FOR REVIEW";
    setRevStatus(next);
    setRevNote("");
  };
  const submitRevision = async () => {
    if (!revModal) return;
    setRevBusy(true);
    try {
      await api.post(
        `/projects/${projectId}/deliverables/${encodeURIComponent(revModal.code)}/bump-revision`,
        { new_status: revStatus, note: revNote },
      );
      setRevModal(null);
      await load();
    } catch (e) {
      alert("Bump revision failed: " +
            (formatErr(e.response?.data?.detail) || e.message));
    } finally {
      setRevBusy(false);
    }
  };

  const openHistory = async (row) => {
    setHistoryBusy(true);
    setDiffData(null);
    try {
      const r = await api.get(
        `/projects/${projectId}/deliverables/${encodeURIComponent(row.code)}/revision-history`);
      const revs = r.data?.revisions || [];
      // Pre-select last two versions for a quick diff
      const fromV = revs.length >= 2 ? revs[revs.length - 2].version : null;
      const toV = revs.length >= 1 ? revs[revs.length - 1].version : null;
      setHistoryModal({
        code: row.code, name: row.name, revisions: revs,
        selectedFrom: fromV, selectedTo: toV,
      });
    } catch (e) {
      alert("Could not load revision history: " +
            (formatErr(e.response?.data?.detail) || e.message));
    } finally {
      setHistoryBusy(false);
    }
  };

  const runDiff = async () => {
    if (!historyModal?.selectedFrom || !historyModal?.selectedTo) return;
    if (historyModal.selectedFrom === historyModal.selectedTo) {
      alert("Pick two different versions to compare.");
      return;
    }
    setDiffBusy(true);
    try {
      const r = await api.get(
        `/projects/${projectId}/deliverables/${encodeURIComponent(historyModal.code)}/diff`,
        { params: {
            from_version: historyModal.selectedFrom,
            to_version: historyModal.selectedTo,
          },
        },
      );
      setDiffData(r.data);
    } catch (e) {
      alert("Diff failed: " +
            (formatErr(e.response?.data?.detail) || e.message));
    } finally {
      setDiffBusy(false);
    }
  };

  const runBulkIssue = async () => {
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const r = await api.post(
        `/projects/${projectId}/bulk-issue`,
        {
          target_status: bulkForm.target_status,
          transmittal_purpose: bulkForm.transmittal_purpose,
          eligible_statuses: [bulkForm.eligible_status],
          to_company: bulkForm.to_company,
          to_person: bulkForm.to_person,
          from_person: bulkForm.from_person,
          note: bulkForm.note,
        },
      );
      setBulkResult(r.data);
      await load();
    } catch (e) {
      alert("Bulk issue failed: " +
            (formatErr(e.response?.data?.detail) || e.message));
    } finally {
      setBulkBusy(false);
    }
  };

  // Drawing viewer — opens fullscreen modal with pan/zoom for the given
  // deliverable code. For multi-drawing codes (D.4.1 isos, F.4.1 loops)
  // it fetches the index first, then passes a side rail to the viewer.
  const isDrawingCode = (code) =>
    ["D.2.1", "D.4.1", "E.3.1", "F.4.1"].includes(code);

  const openViewer = async (row) => {
    const code = row.code;
    const baseTitle = `${code} · ${row.name}`;
    if (code === "D.2.1") {
      setViewer({
        title: code, subtitle: row.name,
        src: `/projects/${projectId}/plot-plan/svg-export`,
        downloadHref: `/projects/${projectId}/plot-plan/svg-export`,
      });
      return;
    }
    if (code === "E.3.1") {
      setViewer({
        title: code, subtitle: row.name,
        src: `/projects/${projectId}/sld/svg-export`,
        downloadHref: `/projects/${projectId}/sld/svg-export`,
      });
      return;
    }
    if (code === "D.4.1") {
      try {
        const r = await api.get(`/projects/${projectId}/isometrics`);
        const items = (r.data?.isometrics || []).map((iso) => ({
          id: iso.id,
          label: iso.line_number || iso.id,
          src: `/projects/${projectId}/isometrics/${iso.id}/svg`,
          downloadHref: `/projects/${projectId}/isometrics/${iso.id}/svg`,
        }));
        if (!items.length) { alert("No isometrics generated yet."); return; }
        setViewer({ title: code, subtitle: row.name, items });
      } catch (e) {
        alert("Could not load isometrics: " +
              (formatErr(e.response?.data?.detail) || e.message));
      }
      return;
    }
    if (code === "F.4.1") {
      try {
        const r = await api.get(`/projects/${projectId}/loop-diagrams`);
        const items = (r.data?.loops || []).map((lp) => ({
          id: lp.id,
          label: lp.instrument_tag || lp.id,
          src: `/projects/${projectId}/loop-diagrams/${lp.id}/svg`,
          downloadHref: `/projects/${projectId}/loop-diagrams/${lp.id}/svg`,
        }));
        if (!items.length) { alert("No loop diagrams generated yet."); return; }
        setViewer({ title: code, subtitle: row.name, items });
      } catch (e) {
        alert("Could not load loop diagrams: " +
              (formatErr(e.response?.data?.detail) || e.message));
      }
      return;
    }
    void baseTitle;  // future: more codes
  };

  return (
    <div data-testid="documents-register-page" style={{
      minHeight: "100vh",
      background: "linear-gradient(180deg,#0B1220 0%,#0A0F1A 100%)",
      color: "#F3F4F6",
      fontFamily: "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto",
    }}>
      <header style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "14px 20px",
        borderBottom: "1px solid #1F2937",
        background: "rgba(15, 23, 42, 0.7)",
        backdropFilter: "blur(8px)",
        position: "sticky", top: 0, zIndex: 10,
      }}>
        <button
          onClick={() => navigate(`/projects/${projectId}`)}
          data-testid="back-to-workspace-btn"
          style={{
            background: "transparent", border: "1px solid #374151",
            color: "#D1D5DB", padding: "6px 10px", borderRadius: 6,
            display: "inline-flex", alignItems: "center", gap: 6,
            cursor: "pointer", fontSize: 12,
          }}
        >
          <ArrowLeft size={14} /> Workspace
        </button>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: -0.2 }}>
            Documents Register
            <span style={{ color: "#FFB000", marginLeft: 6 }}>·</span>
            <span style={{ color: "#9CA3AF", fontWeight: 500, fontSize: 14, marginLeft: 6 }}>
              {register?.project_name || "—"}
            </span>
          </div>
          <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>
            Project No · {register?.project_number || "—"}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {healthScore && (
          <div
            data-testid="project-health-badge"
            title={`Deliverables ${healthScore.components?.deliverable_progress}% · Issuance ${healthScore.components?.issuance_progress}% · Comments ${healthScore.components?.comments_health}% · Clashes ${healthScore.components?.clash_health}% · CAPEX ${healthScore.components?.capex_confidence}%`}
            style={{
              background: "#0B1220",
              border: `1.5px solid ${healthScore.color}`,
              borderRadius: 999, padding: "5px 14px",
              display: "inline-flex", alignItems: "center", gap: 8,
              fontSize: 12, marginRight: 6,
            }}
          >
            <span style={{
              width: 8, height: 8, borderRadius: "50%",
              background: healthScore.color,
              boxShadow: `0 0 8px ${healthScore.color}`,
            }} />
            <span style={{ color: healthScore.color, fontWeight: 700,
                              fontFamily: "ui-monospace" }}>
              {healthScore.score}/100
            </span>
            <span style={{ color: "#9CA3AF",
                              letterSpacing: 0.5, fontSize: 10.5,
                              textTransform: "uppercase" }}>
              {healthScore.verdict}
            </span>
          </div>
        )}
        {staleSync.total > 0 && (
          <div
            data-testid="stale-deliverables-badge"
            title={`Stale deliverables (need regeneration after upstream edits):\n${
              staleSync.items.slice(0, 12).map((s) => `• ${s.code} — ${s.name}`).join("\n")
            }${staleSync.items.length > 12 ? `\n... and ${staleSync.items.length - 12} more` : ""}`}
            style={{
              background: "#3F1D1D",
              border: "1.5px solid #F59E0B",
              borderRadius: 999, padding: "5px 12px",
              display: "inline-flex", alignItems: "center", gap: 8,
              fontSize: 12, marginRight: 6, cursor: "help",
            }}
          >
            <span style={{
              width: 8, height: 8, borderRadius: "50%",
              background: "#F59E0B",
              boxShadow: "0 0 8px #F59E0B",
              animation: "pulse 2s ease-in-out infinite",
            }} />
            <span style={{ color: "#FCD34D", fontWeight: 700,
                              fontFamily: "ui-monospace" }}>
              {staleSync.total}
            </span>
            <span style={{ color: "#FCD34D",
                              letterSpacing: 0.4, fontSize: 10.5,
                              textTransform: "uppercase" }}>
              Stale
            </span>
          </div>
        )}
        {staleSync.total > 0 && (
          <button
            data-testid="regenerate-stale-btn"
            onClick={regenerateAllStale}
            disabled={regenBusy}
            title="Regenerate all stale deliverables"
            style={{
              background: regenBusy ? "#374151" : "#7C2D12",
              color: "#FED7AA",
              border: "1px solid #F97316",
              padding: "8px 12px", borderRadius: 6, fontSize: 12,
              fontWeight: 600, cursor: regenBusy ? "wait" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 6,
              marginRight: 6,
            }}
          >
            <RefreshCcw size={13} className={regenBusy ? "spin" : ""} />
            {regenBusy ? "Queuing…" : "Regenerate Stale"}
          </button>
        )}
        <a
          href={`${API_BASE}/projects/${projectId}/3d-model`}
          data-testid="download-3d-model-btn"
          style={{
            background: "transparent", border: "1px solid #374151",
            color: "#93C5FD", padding: "8px 12px",
            borderRadius: 6, fontSize: 12, fontWeight: 500,
            textDecoration: "none", display: "inline-flex",
            alignItems: "center", gap: 6,
          }}
          title="Download IFC 3D model"
        >
          <Download size={13} /> 3D Model (IFC)
        </a>
        <button
          onClick={() => setShowDepGraph(true)}
          data-testid="open-dep-graph-btn"
          style={{
            background: "transparent", border: "1px solid #374151",
            color: "#A78BFA", padding: "8px 12px", borderRadius: 6,
            fontSize: 12, fontWeight: 500, cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}
          title="Visualise the deliverable dependency DAG"
        >
          <GitBranch size={13} /> Dependency Graph
        </button>
        <button
          onClick={() => { setBulkResult(null); setBulkModal(true); }}
          data-testid="open-bulk-issue-btn"
          style={{
            background: "#7C2D12", color: "#FED7AA",
            border: "1px solid #F97316",
            padding: "8px 12px", borderRadius: 6, fontSize: 12,
            fontWeight: 600, cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}
          title="Bulk-promote eligible deliverables and emit one transmittal"
        >
          <Send size={13} /> Issue All
        </button>
        <a
          href={packageHref}
          data-testid="download-engineering-package-btn"
          style={{
            background: "#FFB000", color: "#0B1220", padding: "8px 12px",
            borderRadius: 6, fontSize: 12, fontWeight: 600,
            textDecoration: "none", display: "inline-flex",
            alignItems: "center", gap: 6,
          }}
        >
          <Download size={14} /> Engineering Package (ZIP)
        </a>
        <button
          onClick={load}
          data-testid="refresh-register-btn"
          style={{
            background: "transparent", border: "1px solid #374151",
            color: "#D1D5DB", padding: "8px 10px", borderRadius: 6,
            cursor: "pointer", fontSize: 12,
            display: "inline-flex", alignItems: "center", gap: 6,
          }}
          title="Refresh register"
        >
          <RefreshCcw size={13} />
        </button>
      </header>

      <main style={{ padding: "20px", maxWidth: 1600, margin: "0 auto" }}>
        {error && (
          <div data-testid="register-error" style={{
            background: "#7F1D1D", color: "#FEE2E2",
            padding: "10px 14px", borderRadius: 6, marginBottom: 16,
            fontSize: 13,
          }}>{error}</div>
        )}

        {/* Stats */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap",
                       marginBottom: 18 }}>
          <StatCard
            label="Total documents"
            value={register?.total ?? "—"}
            accent="#FFB000"
          />
          {Object.entries(register?.by_status || {}).map(([s, n]) => (
            <StatCard key={s} label={s} value={n} accent="#F3F4F6" />
          ))}
        </div>

        {/* Review / Release-Gate Summary Tiles */}
        {(() => {
          const rows = register?.rows || [];
          const blocked = rows.filter((r) => r.release_blocked).length;
          const approved = rows.filter(
            (r) => !r.release_blocked && r.review_status === "APPROVED").length;
          const pending = rows.filter(
            (r) => !r.release_blocked && r.review_status !== "APPROVED").length;
          return (
            <div
              data-testid="review-summary-tiles"
              style={{ display: "flex", gap: 12, flexWrap: "wrap",
                       marginBottom: 18 }}
            >
              <div
                data-testid="tile-blocked"
                title="Deliverables with rejected audit steps — PDF/IFC downloads blocked"
                style={{
                  padding: "10px 16px", borderRadius: 8,
                  background: "rgba(220,38,38,0.12)",
                  border: "1.5px solid #dc2626", minWidth: 150,
                }}
              >
                <div style={{ fontSize: 11, textTransform: "uppercase",
                              letterSpacing: 0.6, color: "#FCA5A5",
                              marginBottom: 4 }}>
                  🔒 Blocked
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#FCA5A5" }}>
                  {blocked}
                </div>
              </div>
              <div
                data-testid="tile-approved"
                title="Deliverables with all audit steps approved — ready for construction"
                style={{
                  padding: "10px 16px", borderRadius: 8,
                  background: "rgba(22,163,74,0.12)",
                  border: "1.5px solid #16a34a", minWidth: 150,
                }}
              >
                <div style={{ fontSize: 11, textTransform: "uppercase",
                              letterSpacing: 0.6, color: "#86EFAC",
                              marginBottom: 4 }}>
                  ✓ Approved
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#86EFAC" }}>
                  {approved}
                </div>
              </div>
              <div
                data-testid="tile-pending"
                title="Deliverables awaiting audit review"
                style={{
                  padding: "10px 16px", borderRadius: 8,
                  background: "rgba(107,114,128,0.12)",
                  border: "1.5px solid #6B7280", minWidth: 150,
                }}
              >
                <div style={{ fontSize: 11, textTransform: "uppercase",
                              letterSpacing: 0.6, color: "#D1D5DB",
                              marginBottom: 4 }}>
                  ⏳ Pending review
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#D1D5DB" }}>
                  {pending}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Filters */}
        <div style={{
          display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12,
          padding: 12,
          background: "var(--pf-surface, #0F172A)",
          border: "1px solid #1F2937", borderRadius: 8,
        }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by code, name or doc number…"
            data-testid="register-search-input"
            style={{
              flex: 1, minWidth: 220,
              background: "#0B1220", color: "#F3F4F6",
              border: "1px solid #374151", padding: "7px 10px",
              borderRadius: 6, fontSize: 12,
            }}
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="register-status-filter"
            style={{
              background: "#0B1220", color: "#F3F4F6",
              border: "1px solid #374151", padding: "7px 10px",
              borderRadius: 6, fontSize: 12, minWidth: 160,
            }}
          >
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={discFilter}
            onChange={(e) => setDiscFilter(e.target.value)}
            data-testid="register-discipline-filter"
            style={{
              background: "#0B1220", color: "#F3F4F6",
              border: "1px solid #374151", padding: "7px 10px",
              borderRadius: 6, fontSize: 12, minWidth: 200,
            }}
          >
            {disciplines.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>

        {/* Transmittal builder */}
        <details data-testid="transmittal-builder" style={{
          marginBottom: 14, padding: "10px 14px",
          background: "var(--pf-surface, #0F172A)",
          border: "1px solid #1F2937", borderRadius: 8,
        }}>
          <summary style={{ cursor: "pointer", fontSize: 13,
                                color: "#FFB000", fontWeight: 600 }}>
            <Send size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
            Generate Transmittal ({selected.size} selected)
          </summary>
          <div style={{ display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                        gap: 8, marginTop: 12 }}>
            <select
              value={txForm.purpose}
              onChange={(e) => setTxForm({ ...txForm, purpose: e.target.value })}
              data-testid="transmittal-purpose-select"
              style={{ background: "#0B1220", color: "#F3F4F6",
                       border: "1px solid #374151", padding: "7px 10px",
                       borderRadius: 6, fontSize: 12 }}
            >
              <option>For Review</option>
              <option>For Approval</option>
              <option>For Construction</option>
              <option>For Information</option>
              <option>Approved for Construction</option>
            </select>
            <input
              placeholder="To Company"
              value={txForm.to_company}
              onChange={(e) => setTxForm({ ...txForm, to_company: e.target.value })}
              data-testid="transmittal-to-company"
              style={{ background: "#0B1220", color: "#F3F4F6",
                       border: "1px solid #374151", padding: "7px 10px",
                       borderRadius: 6, fontSize: 12 }}
            />
            <input
              placeholder="To Person"
              value={txForm.to_person}
              onChange={(e) => setTxForm({ ...txForm, to_person: e.target.value })}
              data-testid="transmittal-to-person"
              style={{ background: "#0B1220", color: "#F3F4F6",
                       border: "1px solid #374151", padding: "7px 10px",
                       borderRadius: 6, fontSize: 12 }}
            />
            <input
              placeholder="From Person"
              value={txForm.from_person}
              onChange={(e) => setTxForm({ ...txForm, from_person: e.target.value })}
              data-testid="transmittal-from-person"
              style={{ background: "#0B1220", color: "#F3F4F6",
                       border: "1px solid #374151", padding: "7px 10px",
                       borderRadius: 6, fontSize: 12 }}
            />
            <button
              onClick={generateTransmittal}
              disabled={transmittalBusy || selected.size === 0}
              data-testid="generate-transmittal-btn"
              style={{
                background: transmittalBusy ? "#6B7280" : "#FFB000",
                color: "#0B1220", padding: "8px 14px",
                border: "none", borderRadius: 6, fontSize: 12,
                fontWeight: 600,
                cursor: transmittalBusy || selected.size === 0
                          ? "not-allowed" : "pointer",
              }}
            >
              {transmittalBusy ? "Generating…" : `Generate (${selected.size})`}
            </button>
          </div>
          {transmittalResult && (
            <div data-testid="transmittal-result" style={{
              marginTop: 10, padding: 10,
              background: "#14532D", color: "#86EFAC",
              border: "1px solid #22C55E", borderRadius: 6, fontSize: 12,
            }}>
              ✓ Transmittal <b>{transmittalResult.transmittal_number}</b>
              {" "}generated · {transmittalResult.purpose} ({transmittalResult.purpose_code})
              {" "}· {transmittalResult.total_documents} documents
            </div>
          )}
        </details>

        {/* Table */}
        <div style={{
          background: "var(--pf-surface, #0F172A)",
          border: "1px solid #1F2937", borderRadius: 8, overflow: "hidden",
        }}>
          {loading ? (
            <div style={{ padding: 30, textAlign: "center", color: "#9CA3AF" }}>
              Loading documents…
            </div>
          ) : filteredRows.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "#9CA3AF" }}>
              No documents match the current filters.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table data-testid="documents-register-table"
                       style={{ width: "100%", borderCollapse: "collapse",
                                 fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#0B1220",
                                  color: "#FFB000",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.4, fontSize: 10.5 }}>
                    <th style={{ padding: "10px 12px", textAlign: "left",
                                   width: 32 }}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        data-testid="register-select-all"
                      />
                    </th>
                    <th style={{ padding: "10px 12px", textAlign: "left" }}>Code</th>
                    <th style={{ padding: "10px 12px", textAlign: "left" }}>Discipline</th>
                    <th style={{ padding: "10px 12px", textAlign: "left" }}>Document Number</th>
                    <th style={{ padding: "10px 12px", textAlign: "left" }}>Title</th>
                    <th style={{ padding: "10px 12px", textAlign: "center" }}>Rev</th>
                    <th style={{ padding: "10px 12px", textAlign: "center" }}>Status</th>
                    <th style={{ padding: "10px 12px", textAlign: "center" }} title="Senior reviewer audit-trail decision">Review</th>
                    <th style={{ padding: "10px 12px", textAlign: "center" }}>Last Transmittal</th>
                    <th style={{ padding: "10px 12px", textAlign: "center" }}>Comments</th>
                    <th style={{ padding: "10px 12px", textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r, idx) => (
                    <tr
                      key={r.code}
                      data-testid={`register-row-${r.code}`}
                      style={{
                        borderTop: "1px solid #1F2937",
                        background: idx % 2 ? "rgba(255,255,255,0.02)" : "transparent",
                      }}
                    >
                      <td style={{ padding: "9px 12px" }}>
                        <input
                          type="checkbox"
                          checked={selected.has(r.code)}
                          onChange={() => toggleOne(r.code)}
                          data-testid={`register-select-${r.code}`}
                        />
                      </td>
                      <td style={{ padding: "9px 12px",
                                       fontFamily: "ui-monospace",
                                       color: "#FFB000", fontWeight: 600 }}>
                        {r.code}
                      </td>
                      <td style={{ padding: "9px 12px", color: "#D1D5DB" }}>
                        {r.discipline}
                      </td>
                      <td style={{ padding: "9px 12px",
                                       fontFamily: "ui-monospace",
                                       fontSize: 11, color: "#9CA3AF" }}>
                        {r.doc_number}
                      </td>
                      <td style={{ padding: "9px 12px", color: "#F3F4F6" }}>
                        {r.name}
                      </td>
                      <td style={{ padding: "9px 12px", textAlign: "center",
                                       fontFamily: "ui-monospace",
                                       color: "#D1D5DB" }}>
                        {r.revision}
                      </td>
                      <td style={{ padding: "9px 12px", textAlign: "center" }}>
                        <StatusBadge status={r.status} />
                      </td>
                      <td data-testid={`review-status-${r.code}`}
                          style={{ padding: "9px 12px", textAlign: "center" }}
                          title={r.release_blocked
                            ? `🔒 Release blocked — ${r.rejected_steps} step(s) rejected in audit trail`
                            : r.review_status === "APPROVED"
                              ? `✅ ${r.approved_steps} step(s) approved`
                              : "Pending senior review"}>
                        <span style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: 3,
                            fontSize: 10,
                            fontFamily: "'JetBrains Mono', monospace",
                            fontWeight: 700,
                            letterSpacing: 0.4,
                            background: r.release_blocked ? "#dc2626"
                              : r.review_status === "APPROVED" ? "#16a34a"
                              : "transparent",
                            color: r.release_blocked || r.review_status === "APPROVED"
                              ? "#fff" : "#6B7280",
                            border: !r.release_blocked
                                && r.review_status !== "APPROVED"
                              ? "1px solid #4B5563" : "none",
                          }}>
                          {r.release_blocked ? "🔒 BLOCKED"
                            : r.review_status === "APPROVED" ? "✓ APPROVED"
                            : "PENDING"}
                        </span>
                      </td>
                      <td style={{ padding: "9px 12px", textAlign: "center",
                                       fontSize: 11, color: "#9CA3AF" }}>
                        {r.last_transmittal
                          ? `${r.last_transmittal.transmittal_number} (${r.last_transmittal.purpose_code})`
                          : "—"}
                      </td>
                      <td style={{ padding: "9px 12px", textAlign: "center",
                                       fontSize: 11 }}>
                        {r.total_comments > 0 ? (
                          <span style={{
                            color: r.open_comments > 0 ? "#F87171" : "#86EFAC",
                          }}>
                            {r.open_comments}/{r.total_comments}
                          </span>
                        ) : (
                          <span style={{ color: "#6B7280" }}>0</span>
                        )}
                      </td>
                      <td style={{ padding: "9px 12px", textAlign: "right",
                                       whiteSpace: "nowrap" }}>
                        {isDrawingCode(r.code) && (
                          <button
                            onClick={() => openViewer(r)}
                            data-testid={`register-view-${r.code}`}
                            title="Open drawing in fullscreen viewer"
                            style={{
                              background: "transparent",
                              border: "1px solid #374151",
                              color: "#86EFAC", padding: "4px 8px",
                              borderRadius: 4, fontSize: 11, cursor: "pointer",
                              marginRight: 6,
                              display: "inline-flex", alignItems: "center", gap: 4,
                            }}
                          >
                            <Eye size={11} /> View
                          </button>
                        )}
                        <button
                          onClick={() => openRevModal(r)}
                          data-testid={`register-bumprev-${r.code}`}
                          title="Bump revision / promote status"
                          style={{
                            background: "transparent",
                            border: "1px solid #374151",
                            color: "#FFB000", padding: "4px 8px",
                            borderRadius: 4, fontSize: 11, cursor: "pointer",
                            marginRight: 6,
                            display: "inline-flex", alignItems: "center", gap: 4,
                          }}
                        >
                          <RefreshCcw size={11} /> Rev
                        </button>
                        <button
                          onClick={() => openHistory(r)}
                          data-testid={`register-history-${r.code}`}
                          title="Revision history & diff viewer"
                          style={{
                            background: "transparent",
                            border: "1px solid #374151",
                            color: "#93C5FD", padding: "4px 8px",
                            borderRadius: 4, fontSize: 11, cursor: "pointer",
                            marginRight: 6,
                            display: "inline-flex", alignItems: "center", gap: 4,
                          }}
                        >
                          <History size={11} /> Hist
                        </button>
                        <a
                          href={pdfHref(r.code)}
                          data-testid={`register-pdf-${r.code}`}
                          onClick={(e) => {
                            e.preventDefault();
                            setPdfPreview({
                              code: r.code,
                              title: r.title || r.name,
                              src:   pdfHref(r.code),
                            });
                          }}
                          style={{
                            color: "#FFB000", textDecoration: "none",
                            display: "inline-flex", alignItems: "center",
                            gap: 4, fontSize: 11, cursor: "pointer",
                          }}
                          title="Open production PDF in fullscreen viewer"
                        >
                          <FileText size={12} /> PDF
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ marginTop: 12, fontSize: 11, color: "#6B7280" }}>
          {filteredRows.length} of {register?.total || 0} documents shown
          {selected.size > 0 ? ` · ${selected.size} selected` : ""}
        </div>

        {revModal && (
          <div
            data-testid="bump-revision-modal"
            onClick={() => !revBusy && setRevModal(null)}
            style={{
              position: "fixed", inset: 0, zIndex: 50,
              background: "rgba(2, 6, 23, 0.78)",
              backdropFilter: "blur(4px)",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 20,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#0F172A",
                border: "1px solid #1F2937",
                borderRadius: 10,
                padding: 22, maxWidth: 480, width: "100%",
                color: "#F3F4F6", boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
              }}
            >
              <div style={{ fontSize: 11, color: "#FFB000",
                              letterSpacing: 1, textTransform: "uppercase",
                              marginBottom: 4 }}>
                Bump Revision · {revModal.code}
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6,
                              color: "#F3F4F6" }}>
                {revModal.name}
              </div>
              <div style={{ fontSize: 12, color: "#9CA3AF",
                              marginBottom: 16 }}>
                Current revision: <b style={{ fontFamily: "ui-monospace",
                                                  color: "#D1D5DB" }}>
                  {revModal.currentRev}
                </b> · A new revision will be inserted with the prior
                content frozen for audit.
              </div>
              <label style={{ fontSize: 11, color: "#9CA3AF",
                                display: "block", marginBottom: 4 }}>
                Promote status to
              </label>
              <select
                value={revStatus}
                onChange={(e) => setRevStatus(e.target.value)}
                data-testid="bumprev-status-select"
                style={{
                  width: "100%",
                  background: "#0B1220", color: "#F3F4F6",
                  border: "1px solid #374151", padding: "8px 10px",
                  borderRadius: 6, fontSize: 13, marginBottom: 14,
                }}
              >
                {STATUS_OPTIONS.filter((s) => s !== "ALL").map((s) =>
                  <option key={s} value={s}>{s}</option>)}
              </select>
              <label style={{ fontSize: 11, color: "#9CA3AF",
                                display: "block", marginBottom: 4 }}>
                Note (optional)
              </label>
              <textarea
                value={revNote}
                onChange={(e) => setRevNote(e.target.value)}
                placeholder="e.g. Incorporated client comments — minor revisions on lines 12–34"
                data-testid="bumprev-note-input"
                style={{
                  width: "100%", minHeight: 70,
                  background: "#0B1220", color: "#F3F4F6",
                  border: "1px solid #374151", padding: "8px 10px",
                  borderRadius: 6, fontSize: 12, marginBottom: 16,
                  resize: "vertical", fontFamily: "inherit",
                }}
              />
              <div style={{ display: "flex", gap: 8,
                              justifyContent: "flex-end" }}>
                <button
                  onClick={() => setRevModal(null)}
                  disabled={revBusy}
                  data-testid="bumprev-cancel-btn"
                  style={{
                    background: "transparent",
                    border: "1px solid #374151",
                    color: "#D1D5DB", padding: "8px 14px",
                    borderRadius: 6, fontSize: 12, cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitRevision}
                  disabled={revBusy}
                  data-testid="bumprev-submit-btn"
                  style={{
                    background: revBusy ? "#6B7280" : "#FFB000",
                    color: "#0B1220", padding: "8px 16px",
                    border: "none", borderRadius: 6, fontSize: 12,
                    fontWeight: 600,
                    cursor: revBusy ? "not-allowed" : "pointer",
                  }}
                >
                  {revBusy ? "Bumping…" : "Bump Revision"}
                </button>
              </div>
            </div>
          </div>
        )}

        {historyModal && (
          <div
            data-testid="history-modal"
            onClick={() => !diffBusy && setHistoryModal(null)}
            style={{
              position: "fixed", inset: 0, zIndex: 50,
              background: "rgba(2, 6, 23, 0.82)",
              backdropFilter: "blur(4px)",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 20,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#0F172A",
                border: "1px solid #1F2937",
                borderRadius: 10,
                padding: 22, maxWidth: 1100, width: "100%",
                maxHeight: "88vh", overflowY: "auto",
                color: "#F3F4F6",
                boxShadow: "0 20px 50px rgba(0,0,0,0.6)",
              }}
            >
              <div style={{ display: "flex",
                              alignItems: "center",
                              gap: 10, marginBottom: 4 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: "#93C5FD",
                                    letterSpacing: 1, textTransform: "uppercase" }}>
                    Revision History · {historyModal.code}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>
                    {historyModal.name}
                  </div>
                </div>
                <button
                  onClick={() => setHistoryModal(null)}
                  data-testid="history-close-btn"
                  style={{
                    background: "transparent",
                    border: "1px solid #374151",
                    color: "#D1D5DB",
                    padding: 6, borderRadius: 6, cursor: "pointer",
                  }}
                >
                  <X size={14} />
                </button>
              </div>
              <div style={{ fontSize: 12, color: "#9CA3AF",
                              marginBottom: 12 }}>
                Pick any two versions and click <b>Compare</b> for a
                line-by-line diff (added in green, removed in red).
              </div>

              <div style={{ overflowX: "auto",
                              border: "1px solid #1F2937",
                              borderRadius: 8, marginBottom: 14 }}>
                <table data-testid="history-revisions-table" style={{
                  width: "100%", borderCollapse: "collapse",
                  fontSize: 12,
                }}>
                  <thead>
                    <tr style={{ background: "#0B1220",
                                    color: "#FFB000",
                                    textTransform: "uppercase",
                                    letterSpacing: 0.4, fontSize: 10.5 }}>
                      <th style={{ padding: "8px 10px", textAlign: "center" }}>From</th>
                      <th style={{ padding: "8px 10px", textAlign: "center" }}>To</th>
                      <th style={{ padding: "8px 10px", textAlign: "center" }}>Version</th>
                      <th style={{ padding: "8px 10px", textAlign: "center" }}>Rev</th>
                      <th style={{ padding: "8px 10px", textAlign: "center" }}>Status</th>
                      <th style={{ padding: "8px 10px", textAlign: "left" }}>By</th>
                      <th style={{ padding: "8px 10px", textAlign: "left" }}>At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyModal.revisions.map((rev) => (
                      <tr key={rev.version}
                          data-testid={`history-rev-row-${rev.version}`}
                          style={{ borderTop: "1px solid #1F2937" }}>
                        <td style={{ padding: "6px 10px", textAlign: "center" }}>
                          <input
                            type="radio"
                            name="diff-from"
                            checked={historyModal.selectedFrom === rev.version}
                            onChange={() => setHistoryModal({
                              ...historyModal,
                              selectedFrom: rev.version,
                            })}
                            data-testid={`history-from-${rev.version}`}
                          />
                        </td>
                        <td style={{ padding: "6px 10px", textAlign: "center" }}>
                          <input
                            type="radio"
                            name="diff-to"
                            checked={historyModal.selectedTo === rev.version}
                            onChange={() => setHistoryModal({
                              ...historyModal,
                              selectedTo: rev.version,
                            })}
                            data-testid={`history-to-${rev.version}`}
                          />
                        </td>
                        <td style={{ padding: "6px 10px", textAlign: "center",
                                         fontFamily: "ui-monospace",
                                         color: "#D1D5DB" }}>
                          v{rev.version}
                        </td>
                        <td style={{ padding: "6px 10px", textAlign: "center",
                                         fontFamily: "ui-monospace",
                                         color: "#FFB000" }}>
                          {rev.revision_label || "—"}
                        </td>
                        <td style={{ padding: "6px 10px", textAlign: "center" }}>
                          <StatusBadge status={rev.status || "DRAFT"} />
                        </td>
                        <td style={{ padding: "6px 10px",
                                         color: "#9CA3AF", fontSize: 11 }}>
                          {rev.updated_by || "—"}
                        </td>
                        <td style={{ padding: "6px 10px",
                                         color: "#9CA3AF", fontSize: 11,
                                         fontFamily: "ui-monospace" }}>
                          {rev.updated_at
                            ? rev.updated_at.replace("T", " ").slice(0, 16)
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ display: "flex", gap: 10,
                              justifyContent: "flex-end",
                              marginBottom: 12 }}>
                <button
                  onClick={runDiff}
                  disabled={diffBusy
                    || !historyModal.selectedFrom
                    || !historyModal.selectedTo
                    || historyModal.selectedFrom === historyModal.selectedTo}
                  data-testid="history-compare-btn"
                  style={{
                    background: diffBusy ? "#6B7280" : "#FFB000",
                    color: "#0B1220", padding: "8px 16px",
                    border: "none", borderRadius: 6, fontSize: 12,
                    fontWeight: 600,
                    cursor: diffBusy ? "not-allowed" : "pointer",
                  }}
                >
                  {diffBusy ? "Comparing…" : "Compare ↔"}
                </button>
              </div>

              {diffData && (
                <div data-testid="diff-viewer" style={{
                  background: "#020617",
                  border: "1px solid #1F2937",
                  borderRadius: 8, overflow: "hidden",
                }}>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 16,
                    padding: "8px 12px", background: "#0B1220",
                    borderBottom: "1px solid #1F2937", fontSize: 12,
                  }}>
                    <span style={{ color: "#9CA3AF" }}>
                      <span style={{ color: "#FCA5A5" }}>v{diffData.from.version} ({diffData.from.revision_label})</span>
                      {" → "}
                      <span style={{ color: "#86EFAC" }}>v{diffData.to.version} ({diffData.to.revision_label})</span>
                    </span>
                    <span style={{ flex: 1 }} />
                    <span style={{ color: "#86EFAC" }}>+{diffData.stats.added}</span>
                    <span style={{ color: "#FCA5A5" }}>−{diffData.stats.removed}</span>
                    <span style={{ color: "#6B7280" }}>
                      ({diffData.stats.total_from} → {diffData.stats.total_to} lines)
                    </span>
                  </div>
                  <pre style={{
                    margin: 0, padding: 0, fontSize: 11.5,
                    fontFamily: "ui-monospace, JetBrains Mono, monospace",
                    maxHeight: "44vh", overflowY: "auto",
                  }}>
                    {diffData.diff.map((d, i) => {
                      const bg = d.type === "add"
                        ? "rgba(34,197,94,0.10)"
                        : d.type === "remove"
                          ? "rgba(239,68,68,0.10)"
                          : "transparent";
                      const color = d.type === "add"
                        ? "#86EFAC"
                        : d.type === "remove"
                          ? "#FCA5A5"
                          : "#9CA3AF";
                      const prefix = d.type === "add"
                        ? "+ "
                        : d.type === "remove"
                          ? "- "
                          : "  ";
                      return (
                        <div key={i} style={{
                          background: bg, color, padding: "1px 12px",
                          whiteSpace: "pre-wrap", wordBreak: "break-word",
                          borderLeft: d.type === "add"
                            ? "3px solid #22C55E"
                            : d.type === "remove"
                              ? "3px solid #EF4444"
                              : "3px solid transparent",
                        }}>
                          {prefix}{d.text || " "}
                        </div>
                      );
                    })}
                  </pre>
                </div>
              )}
              {!diffData && !diffBusy && (
                <div style={{ fontSize: 12, color: "#6B7280",
                                  textAlign: "center", padding: 16 }}>
                  Select a <b>From</b> and <b>To</b> radio in two
                  different rows above, then click Compare.
                </div>
              )}
            </div>
          </div>
        )}

        {bulkModal && (
          <div
            data-testid="bulk-issue-modal"
            onClick={() => !bulkBusy && setBulkModal(false)}
            style={{
              position: "fixed", inset: 0, zIndex: 50,
              background: "rgba(2, 6, 23, 0.82)",
              backdropFilter: "blur(4px)",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 20,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#0F172A",
                border: "1px solid #1F2937",
                borderRadius: 10,
                padding: 24, maxWidth: 620, width: "100%",
                maxHeight: "90vh", overflowY: "auto",
                color: "#F3F4F6",
                boxShadow: "0 20px 50px rgba(0,0,0,0.6)",
              }}
            >
              <div style={{ fontSize: 11, color: "#F97316",
                              letterSpacing: 1, textTransform: "uppercase",
                              marginBottom: 4 }}>
                Bulk Issue
              </div>
              <div style={{ fontSize: 18, fontWeight: 700,
                              marginBottom: 6 }}>
                Issue All for Construction
              </div>
              <div style={{ fontSize: 12, color: "#9CA3AF",
                              marginBottom: 18 }}>
                Sweeps every deliverable in the eligible status, bumps it
                to the target, and emits one consolidated transmittal.
              </div>
              <div style={{ display: "grid", gap: 10,
                              gridTemplateColumns: "1fr 1fr",
                              marginBottom: 16 }}>
                <div>
                  <label style={{ fontSize: 11, color: "#9CA3AF",
                                       display: "block", marginBottom: 4 }}>
                    Eligible (current)
                  </label>
                  <select
                    value={bulkForm.eligible_status}
                    onChange={(e) => setBulkForm({ ...bulkForm,
                      eligible_status: e.target.value })}
                    data-testid="bulk-eligible-status"
                    style={{
                      width: "100%",
                      background: "#0B1220", color: "#F3F4F6",
                      border: "1px solid #374151", padding: "8px 10px",
                      borderRadius: 6, fontSize: 12,
                    }}
                  >
                    {STATUS_OPTIONS.filter((s) => s !== "ALL").map((s) =>
                      <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 11, color: "#9CA3AF",
                                       display: "block", marginBottom: 4 }}>
                    Promote to (target)
                  </label>
                  <select
                    value={bulkForm.target_status}
                    onChange={(e) => setBulkForm({ ...bulkForm,
                      target_status: e.target.value })}
                    data-testid="bulk-target-status"
                    style={{
                      width: "100%",
                      background: "#0B1220", color: "#F3F4F6",
                      border: "1px solid #374151", padding: "8px 10px",
                      borderRadius: 6, fontSize: 12,
                    }}
                  >
                    {STATUS_OPTIONS.filter((s) => s !== "ALL").map((s) =>
                      <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 11, color: "#9CA3AF",
                                       display: "block", marginBottom: 4 }}>
                    Transmittal purpose
                  </label>
                  <input
                    value={bulkForm.transmittal_purpose}
                    onChange={(e) => setBulkForm({ ...bulkForm,
                      transmittal_purpose: e.target.value })}
                    data-testid="bulk-transmittal-purpose"
                    style={{ width: "100%",
                      background: "#0B1220", color: "#F3F4F6",
                      border: "1px solid #374151", padding: "8px 10px",
                      borderRadius: 6, fontSize: 12,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: "#9CA3AF",
                                       display: "block", marginBottom: 4 }}>
                    From (sender)
                  </label>
                  <input
                    value={bulkForm.from_person}
                    onChange={(e) => setBulkForm({ ...bulkForm,
                      from_person: e.target.value })}
                    data-testid="bulk-from-person"
                    style={{ width: "100%",
                      background: "#0B1220", color: "#F3F4F6",
                      border: "1px solid #374151", padding: "8px 10px",
                      borderRadius: 6, fontSize: 12,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: "#9CA3AF",
                                       display: "block", marginBottom: 4 }}>
                    To Company
                  </label>
                  <input
                    value={bulkForm.to_company}
                    onChange={(e) => setBulkForm({ ...bulkForm,
                      to_company: e.target.value })}
                    data-testid="bulk-to-company"
                    style={{ width: "100%",
                      background: "#0B1220", color: "#F3F4F6",
                      border: "1px solid #374151", padding: "8px 10px",
                      borderRadius: 6, fontSize: 12,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: "#9CA3AF",
                                       display: "block", marginBottom: 4 }}>
                    To Person
                  </label>
                  <input
                    value={bulkForm.to_person}
                    onChange={(e) => setBulkForm({ ...bulkForm,
                      to_person: e.target.value })}
                    data-testid="bulk-to-person"
                    style={{ width: "100%",
                      background: "#0B1220", color: "#F3F4F6",
                      border: "1px solid #374151", padding: "8px 10px",
                      borderRadius: 6, fontSize: 12,
                    }}
                  />
                </div>
              </div>
              <label style={{ fontSize: 11, color: "#9CA3AF",
                                  display: "block", marginBottom: 4 }}>
                Note (optional)
              </label>
              <textarea
                value={bulkForm.note}
                onChange={(e) => setBulkForm({ ...bulkForm,
                  note: e.target.value })}
                placeholder="e.g. Issuing the IFC pack — comments incorporated, sealed for construction."
                data-testid="bulk-note-input"
                style={{ width: "100%", minHeight: 64,
                  background: "#0B1220", color: "#F3F4F6",
                  border: "1px solid #374151", padding: "8px 10px",
                  borderRadius: 6, fontSize: 12, marginBottom: 14,
                  resize: "vertical", fontFamily: "inherit",
                }}
              />
              {bulkResult && (
                <div data-testid="bulk-issue-result" style={{
                  marginBottom: 14, padding: 12,
                  background: bulkResult.issued_count > 0 ? "#14532D" : "#1F2937",
                  color: bulkResult.issued_count > 0 ? "#86EFAC" : "#9CA3AF",
                  border: "1px solid " + (bulkResult.issued_count > 0 ? "#22C55E" : "#374151"),
                  borderRadius: 6, fontSize: 12,
                }}>
                  {bulkResult.issued_count > 0 ? (
                    <>
                      ✓ {bulkResult.issued_count} document{bulkResult.issued_count === 1 ? "" : "s"} promoted to {bulkForm.target_status}.
                      {bulkResult.transmittal && (
                        <> Transmittal <b>{bulkResult.transmittal.transmittal_number}</b> ({bulkResult.transmittal.purpose_code}) issued.</>
                      )}
                    </>
                  ) : (
                    <>{bulkResult.message || "No documents matched the selected filter."}</>
                  )}
                </div>
              )}
              <div style={{ display: "flex", gap: 8,
                              justifyContent: "flex-end" }}>
                <button
                  onClick={() => setBulkModal(false)}
                  disabled={bulkBusy}
                  data-testid="bulk-cancel-btn"
                  style={{
                    background: "transparent",
                    border: "1px solid #374151",
                    color: "#D1D5DB", padding: "8px 14px",
                    borderRadius: 6, fontSize: 12, cursor: "pointer",
                  }}
                >
                  Close
                </button>
                <button
                  onClick={runBulkIssue}
                  disabled={bulkBusy}
                  data-testid="bulk-submit-btn"
                  style={{
                    background: bulkBusy ? "#6B7280" : "#F97316",
                    color: "#0B1220", padding: "8px 16px",
                    border: "none", borderRadius: 6, fontSize: 12,
                    fontWeight: 600,
                    cursor: bulkBusy ? "not-allowed" : "pointer",
                  }}
                >
                  {bulkBusy ? "Issuing…" : "Issue Now"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      <DrawingViewerModal
        open={!!viewer}
        onClose={() => setViewer(null)}
        title={viewer?.title}
        subtitle={viewer?.subtitle}
        src={viewer?.src}
        items={viewer?.items}
        downloadHref={viewer?.downloadHref}
      />
      {showDepGraph && (
        <DependencyGraphView
          projectId={projectId}
          onClose={() => setShowDepGraph(false)}
        />
      )}
      {pdfPreview && (
        <PdfPreviewModal
          code={pdfPreview.code}
          title={pdfPreview.title}
          src={pdfPreview.src}
          onClose={() => setPdfPreview(null)}
        />
      )}
    </div>
  );
}
